/* eslint-disable react/destructuring-assignment */
import React from "react";
import { recordReactError } from "@shared/utils/errors/errorLogger";
import BrowserCompatibilityHeuristics from "@shared/utils/browser/BrowserCompatibilityHeuristics";

interface Props {
  fallback: React.ReactNode;
  children?: React.ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * `ErrorBoundary` is a React component designed to catch uncaught errors thrown by a React component in its child component tree.
 *
 * Basic fault-tolerance says that when one system breaks, other systems should not be affected. A good example is the event detail page.
 * The most critical features there can probably be summed down to a few things:
 * - Adding tickets to the cart / purchasing
 * - Knowing what the event is in the first place
 * - Knowing when the event is happening
 * - Knowing where the event is
 *
 * Anything else that might be secondary to that:
 * - Google maps location redirects
 * - More events upsells
 * - Hotel accommodations
 *
 * should not block a user from performing the critical features. That is what the EventBoundarys will do. It allows some errors
 * to be tolerated such as those thrown by secondary features while simultaneously permitting for a crash to happen should a critical feature fail to work.
 *
 * This component is different from error toasts and banners. Error toasts or banners should be used for notifying a user of an error, and the error is often recoverable. Think
 * form validation logic, declined payments, authentication issues, action specific errors like "Failed to upload document" etc.
 * You can think of them as fireproof doors vs fire alarms. A fireproof door prevents fires from spreading (ErrorBoundary), while a
 * fire alarm warns everyone of a _possible_ fire.
 *
 * It should also be noted that this is not a silver bullet, for example Error boundaries do not catch errors inside event handlers, asynchronous code
 * (e.g., `setTimeout`), or server-side rendering (SSR).
 *
 * @example
 * const ProblematicComponent = () => {
 *   // This component throws an error for demonstration
 *   throw new Error('Something went wrong!');
 * };
 *
 * const App = () => (
 *   <ErrorBoundary
 *     fallback={<div>Oops! Something went wrong. Please try again later.</div>}
 *   >
 *     <ProblematicComponent />
 *   </ErrorBoundary>
 * );
 *
 * @see https://reactjs.org/docs/error-boundaries.html
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    // Define a state variable to track whether is an error or not
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error) {
    // Update state so the next render will show the fallback UI

    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // By default, React will log all errors to the console.
    // console.error();

    // Log error
    recordReactError(error, errorInfo, {
      mechanism: { handled: true },
    });

    if (BrowserCompatibilityHeuristics.isErrorDueToOutdatedBrowser(error)) {
      window.location.href = "/unsupported-browser.html";
    }
  }

  render() {
    // Check if the error is thrown
    if (this.state.hasError) {
      return this.props.fallback;
    }

    // Return children components in case of no error
    return this.props.children;
  }
}

export default ErrorBoundary;
